import "./expenseOverviewPiechart.css";
import ReactApexChart from "react-apexcharts";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from 'react-redux';

function ConvertHelper(currencyFrom, currencyTo, amount, forex) {
  const currencyFromRate = forex[currencyFrom];
  const currencyToRate = forex[currencyTo];
  const amountConvertedUSD = amount / currencyFromRate;
  const amountConvertTo = amountConvertedUSD * currencyToRate;
  return amountConvertTo;
}

const ExpenseOverviewPieChart = (props) => {
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [totalAmount, setTotalAmount] = useState([]);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));
  const settingsCurrency = settings?.currency;
  
  // Use refs to track previous values and prevent infinite loops
  const previousExpenseDataRef = useRef(null);
  const previousAllCurrencyRef = useRef(null);
  const previousSelectedCurrencyRef = useRef(null);
  const previousForexRef = useRef(null);
  const previousSettingsCurrencyRef = useRef(null);

  useEffect(() => {
    // Compare arrays/objects to check if content has actually changed
    const arraysEqual = (a, b) => {
      if (!a || !b) return a === b;
      if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
      if (a.length !== b.length) return false;
      return a.every((val, idx) => {
        if (val && typeof val === 'object' && b[idx] && typeof b[idx] === 'object') {
          return JSON.stringify(val) === JSON.stringify(b[idx]);
        }
        return val === b[idx];
      });
    };

    const expenseDataChanged = !arraysEqual(previousExpenseDataRef.current, props.expenseData);
    const allCurrencyChanged = previousAllCurrencyRef.current !== props.allCurrency;
    const selectedCurrencyChanged = previousSelectedCurrencyRef.current !== props.selectedCurrency;
    const forexChanged = previousForexRef.current !== forex;
    const settingsCurrencyChanged = previousSettingsCurrencyRef.current !== settingsCurrency;

    // Only update if something actually changed
    if (expenseDataChanged || allCurrencyChanged || selectedCurrencyChanged || forexChanged || settingsCurrencyChanged) {
      const piChart = () => {
        let expenses = props.allCurrency
          ? props.expenseData
          : props.expenseData.filter(item => item.exp_currency === props.selectedCurrency);

        let result = [];
        let sum = 0;

        expenses.forEach((entry) => {
          const convertedAmount = ConvertHelper(entry.exp_currency, props.selectedCurrency || settingsCurrency, entry.exp_amount, forex);
          sum += convertedAmount;

          const existingEntry = result.find((item) => item.name === entry.exp_category?.exp_category_name);
          if (existingEntry) {
            existingEntry.expenseAmount += convertedAmount;
          } else {
            result.push({ name: entry.exp_category?.exp_category_name, expenseAmount: convertedAmount });
          }
        });

        const updatedData = result.map(item => {
          return { name: item.name, expenseAmount: item.expenseAmount };
        });

        setExpenseCategories(updatedData);
        setTotalAmount(sum);
      };

      piChart();

      // Update refs
      previousExpenseDataRef.current = props.expenseData ? [...props.expenseData] : props.expenseData;
      previousAllCurrencyRef.current = props.allCurrency;
      previousSelectedCurrencyRef.current = props.selectedCurrency;
      previousForexRef.current = forex;
      previousSettingsCurrencyRef.current = settingsCurrency;
    }
  }, [props.allCurrency, props.expenseData, props.selectedCurrency, forex, settingsCurrency]);

  // Move these inside the useEffect so they update when expenseCategories change
  const series = expenseCategories.map((expAmt) => expAmt.expenseAmount);
  const expCategoryNames = expenseCategories.map((expAmt) => expAmt.name);

  const options = {
    chart: {
      width: 188,
      height: 188,
      type: "donut",
    },
    plotOptions: {
      pie: {
        startAngle: -45,
        endAngle: 315,
        expandOnClick: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: "gradient",
    },
    legend: {
      show: false,
    },
    labels: expCategoryNames,
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return val + "%";
        },
      },
      custom: function ({ seriesIndex }) {
        const expenceCat = expCategoryNames[seriesIndex];
        const expenseAmount = expenseCategories[seriesIndex]?.expenseAmount || 0;
        const totalAmts = expenseCategories.reduce(
          (total, project) => total + project.expenseAmount,
          0
        );
        const percentage = ((expenseAmount / totalAmts) * 100).toFixed(2);

        return `<div class="apexcharts-tooltip-custom" style="background: #FFFFFF; box-shadow: 0px 1px 8px rgba(110, 110, 110, 0.1); border-radius: 0px;height: 44pxxpx;padding: 5px 10px;">
                  <span class="apexcharts-tooltip-marker"></span>
                  <span class="apexcharts-tooltip-text" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${expenceCat}</span>
                  <span class="apexcharts-tooltip-value" style="font-weight: 300; font-size: 11px; line-height: 13.42px; letter-spacing: 0.5px; color: #282828;">${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: props.selectedCurrency || settings.currency
        }).format(expenseAmount)} - ${percentage}%</span>
                </div>`;
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
    colors: [
      "#8AA9D2", "#234266", "#6D98CF", "#4263A5", "#6BA9E2", "#9ACBFB", "#75AECF", "#3f4b57",
      "#6A9DCB", "#A4C4E6", "#9AC9F3", "#000", "#89A7C4", "#8EA9C6", "#BBCFE7", "#B7D4F0",
      "#A6C0D6", "#4B6F98", "#A2B5CB", "#86A0BC"
    ], // Set the bar colors
    states: {
      active: {
        filter: {
          type: "none", // No visual change for active state
        },
      },
      normal: {
        filter: {
          type: "none", // No visual change for normal state
        },
      },
    },
  };
  const allNegative = series.every(value => value <= 0);
  const [showMessage, setShowMessage] = useState(false);
  useEffect(() => {
    if (allNegative) {
      // Set a delay of 2 seconds before showing the message
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 2000);

      // Clear the timer if component unmounts
      return () => clearTimeout(timer);
    }
  }, [allNegative]);

  // Check if we should show skeleton loader
  const isLoading = props.isLoading;

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
      <div className="pie-outer">
        <div className="pie-chart-top">
          <div className="top-graph">
            <div className="top-inner-graph">
              {isLoading ? (
                <>
                  <div
                    style={{
                      width: "120px",
                      height: "22px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                  <div
                    style={{
                      width: "180px",
                      height: "24px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </>
              ) : (
                <>
                  <div className="graph-title">Per Category</div>
                  <div className="icon-graph">{new Intl.NumberFormat('en-US', { style: 'currency', currency: props.selectedCurrency ? props.selectedCurrency : settings?.currency||"USD" }).format(totalAmount)} </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="pie-chart-inner">
          {isLoading ? (
            <div style={{
              width: '290px',
              height: '188px',
              margin: '0 auto',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {/* Outer donut ring skeleton with colored segments */}
              <div style={{
                width: '188px',
                height: '188px',
                borderRadius: '50%',
                border: '28px solid',
                borderTopColor: '#8AA9D2',
                borderRightColor: '#234266',
                borderBottomColor: '#6D98CF',
                borderLeftColor: '#4263A5',
                opacity: 0.3,
                animation: 'pulse 1.5s ease-in-out infinite',
                position: 'relative',
                transform: 'rotate(-45deg)',
                boxSizing: 'border-box'
              }}>
                {/* Inner circle (donut hole) */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '132px',
                  height: '132px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                }} />
              </div>
            </div>
          ) : allNegative && showMessage ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", padding: "24px" }}>
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="10" width="32" height="26" rx="3" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M6 16h32" stroke="#D0D5DD" strokeWidth="2" />
                <path d="M14 7v6M30 7v6" stroke="#D0D5DD" strokeWidth="2" strokeLinecap="round" />
                <path d="M13 24h6M13 29h12" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <p style={{ margin: 0, fontSize: "15px", fontWeight: "500", color: "#282828" }}>No data to display</p>
            </div>
          ) : (
            <ReactApexChart
              options={options}
              series={series}
              type="donut"
              width={290}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ExpenseOverviewPieChart;